import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import toast from "react-hot-toast";
import resolveImageUrl from "../../utils/imageUtils";
import { motion, AnimatePresence } from "framer-motion";
import { FaClipboardList } from 'react-icons/fa';
import { trackAction, setCurrentSupervisorId } from '../../utils/clickTracking';

const SHOW_ACTIVITY_HISTORY = false;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const Property = () => {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [circles, setCircles] = useState({ all: [], supervisor: [] });
  const [selectedCircle, setSelectedCircle] = useState(searchParams.get("circle") || "");
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewStartTime, setViewStartTime] = useState(null);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState("");
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [propertyTimeline, setPropertyTimeline] = useState([]);

  // New states for filters
  const [activeTab, setActiveTab] = useState("all"); // all, pending, reviewed, flagged-reassessed, flagged-not-reassessed
  const [currentPage, setCurrentPage] = useState(1);

  // Search state
  const [searchPin, setSearchPin] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [highlightPin, setHighlightPin] = useState("");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const currentUserId = user?.id ?? user?.userId;
    if (currentUserId) {
      setCurrentSupervisorId(currentUserId);
      trackAction('property_view', null, null, {}, 'supervisor');
    }
  }, []);

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const getPt5Reference = (property) =>
    property?.refno ?? property?.pt5 ?? property?.pt_5 ?? property?.pt ?? "";

  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  };

  const getCirclesWithML = (allCircles) => {
    const halfIndex = Math.ceil(allCircles.length / 2);
    return allCircles.slice(0, halfIndex);
  };

  // Find the first usable admin value from a list of possible column names
  const getAdminValue = (property, keys) => {
    console.log('getAdminValue called with keys:', keys, 'property:', property);
    for (const key of keys) {
      const value = property?.[key];
      console.log(`Checking key ${key}: value =`, value, 'hasUsableValue =', hasUsableValue(value));
      if (hasUsableValue(value)) return value;
    }
    console.log('No usable value found for keys', keys);
    return null;
  };

  const generateMockMLData = (property, hasML) => {
    if (!hasML) {
      return {
        ...property,
        floors_pred: null,
        land_use_pred: null,
        landarea_combined_pred: null,
        coveredarea_combined_pred: null,
      };
    }

    const floorOptions = [1, 2, 3, 4, 5];
    // const landUseOptions = ["Residential", "Commercial", "Industrial", "Agricultural", "Mixed Use"];
    const landUseOptions = ["Residential", "Commercial", "Mixed Use"];

    return {
      ...property,
      floors_pred: getAdminValue(property, ["floor_admin", "floors_admin", "floor", "floors", "floorAdmin", "floorsAdmin"]) || floorOptions[Math.floor(Math.random() * floorOptions.length)],
      land_use_pred: getAdminValue(property, ["landuse_admin", "land_use_admin", "landuse", "land_use", "landuseAdmin", "landUseAdmin"]) || landUseOptions[Math.floor(Math.random() * landUseOptions.length)],
      landarea_combined_pred: getAdminValue(property, ["landarea_admin", "land_area_admin", "landarea", "land_area", "landareaAdmin", "landAreaAdmin"]) || Math.floor(Math.random() * 500) + 100,
      coveredarea_combined_pred: getAdminValue(property, ["coveredarea_admin", "covered_area_admin", "coveredarea", "covered_area", "coveredareaAdmin", "coveredAreaAdmin"]) || Math.floor(Math.random() * 3000) + 500,
    };
  };

  const fetchCircles = async () => {
    try {
      const res = await axios.get("/properties/circles");
      setCircles({ all: res.data || [], supervisor: [] });
    } catch {
      toast.error("Failed to load circles");
    }
  };

  const [allProperties, setAllProperties] = useState([]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const currentUserId = user?.id ?? user?.userId;

      // Fetch ONLY properties assigned directly to this supervisor
      const allRes = await axios.get("/properties", {
        params: { limit: 10000 },
      });

      const showOnlyAssigned = searchParams.get("assigned") === "true";
      const circleFilter = selectedCircle;

      const allFiltered = dedupeByPin((allRes.data?.data || []).filter((p) => {
        if (circleFilter && circleFilter !== "Uncategorized" && p.circle !== circleFilter) {
          return false;
        }
        if (circleFilter === "Uncategorized" && p.circle) {
          return false;
        }

        // ALWAYS only show properties assigned directly to this supervisor
        if (p.supervisor_user_id !== currentUserId) {
          return false;
        }

        // Once a property has been flagged or accepted from this page, it
        // should no longer show in certain tabs, but keep them for counts
        // Counts (Pending/Reviewed/Flagged) and non-"all" tabs should reflect
        // every property matching the circle filter — not just ones owned by
        // or unclaimed for this supervisor. Ownership only matters when the
        // user explicitly asked to see properties assigned to them.
        if (showOnlyAssigned) {
          return ["assigned", "flagged"].includes(p.status) && p.supervisor_user_id === currentUserId;
        }
        return true;
      }));

      // Filter out flagged/resolved properties for display (and use this for allProperties)
      const filtered = allFiltered.filter((p) => {
        if (["flagged", "resolved", "unresolved"].includes(p.status)) return false;
        return true;
      });

      const allCircles = [...new Set(allFiltered.map((p) => p.circle).filter(Boolean))];
      const circlesWithML = getCirclesWithML(allCircles);
      const supervisorCircles = [...new Set(allFiltered.filter(p => p.supervisor_user_id === currentUserId && p.circle).map(p => p.circle).filter(Boolean))];
      setCircles({ all: allCircles, supervisor: supervisorCircles });

      const allPropertiesWithML = filtered.map((property) => {
        // If property has been reassessed, use its actual values and skip mock data generation
        if (property.reassess === 1 || property.reassess === "1") {
          return property;
        }
        const hasML = circlesWithML.includes(property.circle);
        return generateMockMLData(property, hasML);
      });

      // setAllProperties(allPropertiesWithML);
      const fetchBust = Date.now();
      setAllProperties(allPropertiesWithML.map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
    } catch {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  // Search handler — searches all properties by PIN
  const handleSearchByPin = async () => {
    const pin = searchPin.trim();
    if (!pin) {
      setSearchMessage("Please enter a PIN.");
      return;
    }

    trackAction('property_search', null, null, { searched_pin: pin }, 'supervisor');

    try {
      setSearching(true);
      setSearchMessage("");

      const found = allProperties.find(
        (p) => String(p.pin || '').trim().toLowerCase() === pin.toLowerCase()
      );

      if (!found) {
        setSearchMessage("No property found with this PIN.");
        setSearching(false);
        return;
      }

      setProperties([found]);
      setSearchMessage(`Found 1 property with PIN: ${pin}`);
    } catch (err) {
      console.error(err);
      setSearchMessage("Failed to search property by PIN.");
    } finally {
      setSearching(false);
    }
  };

  // Clear search and reset to all properties
  const handleClearSearch = () => {
    setSearchPin("");
    setSearchMessage("");
    setSelectedCircle("");
    setCurrentPage(1);
    fetchProperties();
  };

  useEffect(() => {
    fetchCircles();
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedCircle) {
      trackAction('property_circle_filter', null, null, { circle: selectedCircle }, 'supervisor');
    }
    setCurrentPage(1);
    fetchProperties();
  }, [selectedCircle, searchParams.get("assigned")]);

  // Auto-open property modal when navigated from dashboard search with ?pin=
  useEffect(() => {
    if (!targetPinFromQuery || !properties.length) return;
    if (autoOpenedPin === targetPinFromQuery) return;

    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === targetPinFromQuery.toLowerCase()
    );

    if (matched) {
      setSelectedProperty(matched);
      setShowPopup(true);
      setViewStartTime(Date.now());
      setAutoOpenedPin(targetPinFromQuery);
      trackAction('property_view_details', matched.pin, null, {}, 'supervisor');
    }
  }, [targetPinFromQuery, properties, autoOpenedPin]);



  // Calculate filtered properties based on activeTab
  const filteredProperties = useMemo(() => {
    const applyTabFilter = (p) => {
      switch (activeTab) {
        case "reviewed":
          return p.reassess === 1 || p.reassess === "1" || p.status === "resolved";
        case "pending":
          return !(p.reassess === 1 || p.reassess === "1" || p.status === "resolved");
        default:
          return true;
      }
    };
    return allProperties.filter(applyTabFilter);
  }, [allProperties, activeTab]);

  // Calculate counts
  const reviewedCount = allProperties.filter((p) => p.reassess === 1 || p.reassess === "1" || p.status === "resolved").length;
  const pendingCount = allProperties.length - reviewedCount;

  // Calculate pagination info for filtered properties
  const totalFilteredPages = Math.ceil(filteredProperties.length / 12);

  // Reset current page if it exceeds total pages for current tab
  useEffect(() => {
    const currentTabTotalPages = getCurrentTabTotalPages();
    if (currentPage > currentTabTotalPages && currentTabTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [activeTab, allProperties, filteredProperties, currentPage]);

  // Calculate total pages for the current active tab
  const getCurrentTabTotalPages = () => {
    if (activeTab === "all") {
      return Math.ceil(allProperties.length / 12);
    } else {
      return totalFilteredPages;
    }
  };

  // Handle page change for all tabs
  const handlePageChange = (newPage) => {
    const currentTabTotalPages = getCurrentTabTotalPages();
    if (newPage >= 1 && newPage <= currentTabTotalPages) {
      const currentPageForTab = activeTab === "all" ? currentPage : currentPage;
      trackAction(newPage > currentPageForTab ? 'property_page_next' : 'property_page_prev', null, null, {}, 'supervisor');
      setCurrentPage(newPage);
    }
  };

  const handlePropertySelect = async (property) => {
    trackAction('property_view_details', property.pin, null, {}, 'supervisor');
    console.log('Selected property object:', property);
    console.log('Property keys:', Object.keys(property));
    console.log('Inspector Explanations:', {
      overall: property.inspector_overall_explanation,
      floor: property.inspector_floor_explanation,
      landuse: property.inspector_landuse_explanation,
      landarea: property.inspector_landarea_explanation,
      coveredarea: property.inspector_coveredarea_explanation,
      createdAt: property.inspector_explanation_created_at,
    });
    setSelectedProperty(property);
    setShowPopup(true);
    setViewStartTime(Date.now());

    try {
      const params = property?.id ? { propertyId: property.id } : {};
      const res = await axios.get(`/properties/timeline/${property.pin}`, { params });
      setPropertyTimeline(res.data || []);
    } catch (err) {
      console.error("Failed to fetch property timeline:", err);
      setPropertyTimeline([]);
    }
  };

  const closePopup = async () => {
    const pinToHighlight = selectedProperty?.pin;
    if (selectedProperty && viewStartTime) {
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      if (userId && selectedProperty.pin) {
        const durationMs = Date.now() - viewStartTime;
        try {
          await sendViewTime(selectedProperty.pin, userId, durationMs, "viewed");
        } catch (err) {
          console.warn("⚠️ Failed to record view time:", err.message);
        }
      }
    }

    setShowPopup(false);
    setSelectedProperty(null);
    setViewStartTime(null);
    highlightPropertyCard(pinToHighlight);
  };

  const handleFlag = (propertyId) => {
    setSelectedProperty((prev) => ({
      ...prev,
      id: propertyId,
      property_id: propertyId,
    }));
    setShowCommentBox(true);
    setComment("");
  };

  const sendViewTime = async (propertyPin, userId, durationMs, option = "viewed") => {
    try {
      await axios.post("/time/view-time", {
        propertyPin,
        userId,
        duration: Math.round(durationMs / 1000),
        option,
      });
    } catch (err) {
      console.error("Failed to record view time", err);
    }
  };

  const submitFlag = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id ?? user?.userId;
    if (!userId) return toast.error("User ID not found");
    if (!comment.trim()) return toast.error("Please enter a reason");

    const propertyId = selectedProperty?.id ?? selectedProperty?.property_id;
    if (!propertyId) return toast.error("Property ID missing");

    try {
      // Track the flag action with the comment
      await trackAction('property_flag', selectedProperty.pin, null, { comment_text: comment }, 'supervisor');

      await axios.post("/properties/flag", {
        propertyId,
        userId,
        comment,
      });

      const durationMs = Date.now() - viewStartTime;
      await sendViewTime(selectedProperty.pin, userId, durationMs, "flagged");

      toast.success("Property flagged successfully");
      fetchProperties(pagination.page);
      setComment("");
      setShowCommentBox(false);
      closePopup();
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.details ||
        "Failed to flag property"
      );
    }
  };

  const handleAccept = async (propertyId) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id ?? user?.userId;
    if (!userId) return toast.error("User ID not found");

    try {
      // Track the accept action
      await trackAction('property_accept', selectedProperty.pin, null, { comment_text: "Supervisor accepted the property" }, 'supervisor');

      const durationMs = Date.now() - viewStartTime;
      await axios.post("/properties/accept", { propertyId, userId });
      await sendViewTime(selectedProperty.pin, userId, durationMs, "accepted");
      toast.success("Property accepted");
      fetchProperties(pagination.page);
      closePopup();
    } catch {
      toast.error("Failed to accept property");
    }
  };

  const hasMLData = (property) => {
    if (!property) return false;
    const candidates = [
      property.floors_ml,
      property.floors_pred,
      property.landuse_ml,
      property.land_use_ml,
      property.land_use_pred,
      property.landarea_ml,
      property.landarea_combined_pred,
      property.coveredarea_ml,
      property.coveredarea_combined_pred,
    ];
    return candidates.some((v) => {
      if (v === null || v === undefined) return false;
      const s = String(v).trim().toLowerCase();
      return s !== "" && s !== "null" && s !== "undefined" && s !== "n/a";
    });
  };

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number' && Number.isFinite(value)) return true; // If it's a valid number, count it!
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();
    switch (normalizedStatus) {
      case "resolved":
        return <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">Resolved</span>;
      case "flagged":
        return <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">Flagged</span>;
      case "assigned":
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">Assigned</span>;
      case "pending":
        return <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">Pending</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">{status || "N/A"}</span>;
    }
  };

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();

    // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

    // Check for "greater than X" or "more than X"
    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }

    // Check for "less than X" or "under X"
    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }

    // Check for "between X and Y"
    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return {
        type: 'between',
        min: Number(match[1]),
        max: Number(match[2])
      };
    }

    // Try to parse as a single number
    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }

    return null;
  };

  const areValuesDifferent = (adminVal, mlVal, field) => {
    const normalize = (val) => {
      if (val === null || val === undefined) return null;
      const raw = String(val).trim();
      if (!raw) return null;
      const lowered = raw.toLowerCase();
      if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
      const numeric = Number(raw.replace(/,/g, ""));
      if (Number.isFinite(numeric)) return numeric;
      return lowered;
    };

    // For landarea and coveredarea, check if admin is within ML range
    if ((field === "landarea" || field === "land_area" || field === "coveredarea" || field === "covered_area") && mlVal) {
      const range = parseRange(mlVal);
      const a = normalize(adminVal);
      if (range && typeof a === "number") {
        switch (range.type) {
          case "greater":
            return a <= range.value; // highlight if admin is NOT greater
          case "less":
            return a >= range.value; // highlight if admin is NOT less
          case "between":
            return a < range.min || a > range.max; // highlight if outside range
          case "exact":
            return Math.abs(a - range.value) > 1e-9; // highlight if different
        }
      }
    }

    // For all other fields, use original comparison
    const a = normalize(adminVal);
    const m = normalize(mlVal);
    if (a === null || m === null) return false;
    if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
    return a !== m;
  };

  const getMLDisplayValue = (property, field) => {
    if (!property) return "N/A";
    if (!hasMLData(property)) return "N/A";
    const pickFirst = (keys) => {
      for (const key of keys) {
        const v = property?.[key];
        if (hasUsableValue(v)) return v;
      }
      return null;
    };
    switch (field) {
      case "floor": {
        const v = pickFirst(["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
        if (!hasUsableValue(v)) return "N/A";
        const num = toFiniteNumber(v);
        if (num === null) return String(v);
        return Number.isInteger(num) ? String(num) : String(Math.round(num));
      }
      case "landuse": {
        const v = pickFirst(["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
        return hasUsableValue(v) ? String(v) : "N/A";
      }
      case "landarea": {
        const ml = pickFirst(["landarea_ml", "land_area_ml"]);
        const bucket = pickFirst(["landarea_combined_pred"]);
        const mlNum = toFiniteNumber(ml);
        const mlIsZeroish = mlNum !== null && mlNum <= 0;
        const v =
          hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish)
            ? bucket
            : (hasUsableValue(ml) ? ml : bucket);
        // Check if realValue is a range string before trying to format as number
        const strVal = String(v);
        const isRange = strVal.toLowerCase().includes("greater") ||
          strVal.toLowerCase().includes("less") ||
          strVal.toLowerCase().includes("between");
        if (isRange) {
          return strVal;
        }
        const formatted = formatTwoDecimals(v);
        return formatted || (hasUsableValue(v) ? String(v) : "N/A");
      }
      case "coveredarea": {
        const ml = pickFirst(["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirst(["coveredarea_combined_pred"]);
        const mlNum = toFiniteNumber(ml);
        const mlIsZeroish = mlNum !== null && mlNum <= 0;
        const v =
          hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish)
            ? bucket
            : (hasUsableValue(ml) ? ml : bucket);
        // Check if realValue is a range string before trying to format as number
        const strVal = String(v);
        const isRange = strVal.toLowerCase().includes("greater") ||
          strVal.toLowerCase().includes("less") ||
          strVal.toLowerCase().includes("between");
        if (isRange) {
          return strVal;
        }
        const formatted = formatTwoDecimals(v);
        return formatted || (hasUsableValue(v) ? String(v) : "N/A");
      }
      default:
        return "N/A";
    }
  };

  const handleImageClick = (e, imageUrl) => {
    e.stopPropagation();
    trackAction('property_image_rotate', selectedProperty?.pin, null, {}, 'supervisor');
    setFullscreenImageUrl(imageUrl);
    setShowFullscreenImage(true);
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      // const bust = Date.now();
      // setProperties((prev) =>
      //   prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      // );
      const bust = Date.now();
      setAllProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const closeFullscreenImage = () => {
    setShowFullscreenImage(false);
    setFullscreenImageUrl("");
    setRotation(0);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-100 to-teal-50">
      <Sidebar />
      <main className="flex-1 p-6 overflow-y-auto">
        <h1 className="text-4xl font-extrabold text-center text-teal-800 mb-4 tracking-wide drop-shadow-sm">
          🏠 Property Management Dashboard
        </h1>

        <div className="max-w-4xl mx-auto mb-6">
          <p className="text-gray-600 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-3 rounded-r">
            Hello! These properties have been reviewed by the inspector. Please go through each and decide if you accept or reject their assessment.
          </p>
        </div>

        {searchParams.get("assigned") === "true" && (
          <div className="max-w-4xl mx-auto mb-6 bg-teal-50 border border-teal-300 rounded-xl p-4 flex items-center justify-center gap-3">
            <FaClipboardList className="text-teal-600 text-xl" />
            <p className="text-lg font-medium text-teal-800">
              Showing only properties assigned to you by Admin
            </p>
            <button
              onClick={() => navigate("/supervisor/property")}
              className="ml-4 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              Show All Properties
            </button>
          </div>
        )}

        <p className="text-center text-gray-700 mb-8 text-lg font-medium">
          Showing <span className="text-teal-700 font-bold">{filteredProperties.length}</span> {
            activeTab === "all" ? "total" :
              activeTab === "pending" ? "pending" :
                activeTab === "reviewed" ? "reviewed" :
                  activeTab === "flagged-reassessed" ? "flagged - reassessed" :
                    "flagged - not reassessed"
          } properties
        </p>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h2 className="text-md font-semibold text-gray-800 mb-3">Search Property by PIN</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={searchPin}
                onChange={(e) => setSearchPin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchByPin();
                  }
                }}
                placeholder="Enter exact PIN..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
              <button
                onClick={handleSearchByPin}
                disabled={searching}
                className="px-5 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
              {searchMessage && searchMessage.includes("Found") && (
                <button
                  onClick={handleClearSearch}
                  className="px-5 py-2 rounded-lg bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
            {searchMessage && (
              <p className={`mt-3 text-sm ${searchMessage.includes("Found") ? "text-green-600" : "text-red-600"}`}>
                {searchMessage}
              </p>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-8 bg-white shadow-md rounded-xl p-4 border border-teal-100">
          {/* Circle Filter */}
          <select
            value={selectedCircle}
            onChange={(e) => setSelectedCircle(e.target.value)}
            className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-gray-50 shadow-sm min-w-[180px]"
          >
            <option value="">All Circles</option>
            {circles.supervisor.length > 0 && (
              <>
                <option disabled style={{ backgroundColor: '#f0f0f0', color: '#666' }}>--- Assigned Circles ---</option>
                {circles.supervisor.map((circle, i) => (
                  <option key={`supervisor-${i}`} value={circle}>
                    {circle}
                  </option>
                ))}
              </>
            )}
            {circles.all.filter(c => !circles.supervisor.includes(c)).length > 0 && (
              <>
                {circles.supervisor.length > 0 && <option disabled style={{ backgroundColor: '#f0f0f0', color: '#666' }}>--- Other Circles ---</option>}
                {circles.all.filter(c => !circles.supervisor.includes(c)).map((circle, i) => (
                  <option key={`other-${i}`} value={circle}>
                    {circle}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        {/* Reviewed/Pending Tabs */}
        <div className="flex border-b border-gray-300 mb-8 bg-white rounded-t-xl">
          <button
            onClick={() => {
              setActiveTab("all");
              setCurrentPage(1);
            }}
            className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === "all"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setActiveTab("pending");
              setCurrentPage(1);
            }}
            className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === "pending"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => {
              setActiveTab("reviewed");
              setCurrentPage(1);
            }}
            className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === "reviewed"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Reviewed ({reviewedCount})
          </button>
        </div>

        {/* Property Grid */}
        {loading ? (
          <div className="text-center text-gray-600 animate-pulse text-lg">
            Loading properties...
          </div>
        ) : filteredProperties.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {(activeTab === "all"
                ? allProperties.slice((currentPage - 1) * 12, currentPage * 12) // For all tab, use local pagination of allProperties
                : filteredProperties.slice((currentPage - 1) * 12, currentPage * 12)
              ).map((property) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  data-pin={normalizePin(property.pin)}
                  className={`bg-white rounded-xl shadow hover:shadow-lg border border-gray-200 overflow-hidden group relative ${normalizePin(property.pin) && normalizePin(property.pin) === highlightPin
                    ? "ring-2 ring-sky-500"
                    : ""
                    } ${property.supervisor_user_id ? "border-l-4 border-l-purple-500" : ""}`}
                >
                  {normalizePin(property.pin) && normalizePin(property.pin) === highlightPin && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}

                  {/* Property Image */}
                  <div
                    className="h-48 bg-gray-100 overflow-hidden cursor-pointer"
                    onClick={() => handlePropertySelect(property)}
                  >
                    {property.image ? (
                      <img
                        src={appendCacheBuster(resolveImageUrl(property.image), property._imgBust)}
                        className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                        alt="Property"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gray-50">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Property Details */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-lg text-gray-900 truncate pr-2">
                        {property.pin}
                      </h3>
                      {property.supervisor_user_id && (
                        <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded-full">Assigned</span>
                      )}
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span>Circle: {property.circle || "N/A"}</span>
                      </div>
                      <div className="mt-2">
                        {getStatusBadge(property.status)}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handlePropertySelect(property)}
                        className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-1"
                      >
                        View Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Unified Pagination */}
            {getCurrentTabTotalPages() > 1 && !searchMessage.includes("Found") && (
              <div className="flex justify-center items-center gap-4 mt-10 flex-wrap">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                >
                  Prev
                </button>

                {Array.from({ length: getCurrentTabTotalPages() }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-3 py-1 rounded-md transition ${currentPage === i + 1
                      ? "bg-teal-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === getCurrentTabTotalPages()}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="text-gray-400 text-6xl mb-4">🏙️</div>
            <p className="text-gray-600 text-lg font-medium">
              No properties found for selected filters.
            </p>
          </div>
        )}

        {/* Property Popup Modal - Updated with new format */}
        <AnimatePresence>
          {showPopup && selectedProperty && (
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", duration: 0.3 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
              >
                {/* Modal Header */}
                <div className="sticky top-0 z-10 flex justify-between items-center p-4 border-b bg-gradient-to-r from-teal-100 to-teal-200">
                  <h3 className="text-2xl font-bold text-teal-800 truncate">
                    Property - {selectedProperty.pin}
                  </h3>
                  <button
                    onClick={closePopup}
                    className="text-gray-600 hover:text-red-600 text-2xl font-bold w-8 h-8 flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>

                {/* Property Image */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden mb-6 relative">
                    {selectedProperty.image ? (
                      <>
                        <div
                          style={{ transform: `rotate(${rotation}deg)` }}
                          className="w-full transition-transform duration-300 ease-in-out"
                        >
                          <img
                            src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                            alt={`Property ${selectedProperty.pin}`}
                            className="w-full h-auto max-h-[400px] object-contain cursor-pointer hover:opacity-90 transition-opacity mx-auto"
                            onClick={(e) =>
                              handleImageClick(e, appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust))
                            }
                          />
                        </div>
                        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                          <button
                            onClick={handleRotate}
                            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                            title="Rotate Image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          {rotation !== 0 && (
                            <button
                              onClick={handleSaveRotation}
                              disabled={savingImageRotation}
                              className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                              title="Save Rotation"
                            >
                              {savingImageRotation ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center text-gray-500 text-lg">
                        No Image
                      </div>
                    )}
                  </div>

                  {selectedProperty.supervisor_flag_comment && (
                    <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-yellow-800">Supervisor Flag Comment</h3>
                      </div>
                      <p className="text-yellow-700 text-sm whitespace-pre-wrap">
                        {selectedProperty.supervisor_flag_comment}
                      </p>
                    </div>
                  )}

                  {/* Inspector Comments Section */}
                  {(selectedProperty.inspector_overall_explanation ||
                    selectedProperty.inspector_floor_explanation ||
                    selectedProperty.inspector_landuse_explanation ||
                    selectedProperty.inspector_landarea_explanation ||
                    selectedProperty.inspector_coveredarea_explanation) && (
                      <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">Inspector Comments</h3>
                          {selectedProperty.inspector_explanation_created_at && (
                            <div className="text-sm text-gray-500">
                              {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                            </div>
                          )}
                        </div>

                        {selectedProperty.inspector_overall_explanation && (
                          <div className="mb-3">
                            <div className="text-sm font-semibold text-gray-700">Overall</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_overall_explanation}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedProperty.inspector_floor_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Floor</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_floor_explanation}
                              </div>
                            </div>
                          )}
                          {selectedProperty.inspector_landuse_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Land Use</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_landuse_explanation}
                              </div>
                            </div>
                          )}
                          {selectedProperty.inspector_landarea_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Land Area</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_landarea_explanation}
                              </div>
                            </div>
                          )}
                          {selectedProperty.inspector_coveredarea_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Covered Area</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_coveredarea_explanation}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                        PIN: {selectedProperty.pin}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                        Circle: {selectedProperty.circle || "N/A"}
                      </span>
                      {/*  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                        PT-5: {getPt5Reference(selectedProperty) || "N/A"}
                      </span> */}
                      {getStatusBadge(selectedProperty.status)}
                    </div>
                    {selectedProperty.reassess == 1 && selectedProperty.reassessment_date && (
                      <div className="mb-3 text-sm text-gray-600">
                        Reassessed At:{" "}
                        <span className="font-semibold text-gray-700">
                          {new Date(selectedProperty.reassessment_date).toLocaleString()}
                        </span>
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <table className="w-full border rounded-lg overflow-hidden">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="p-3 text-left font-medium">Feature</th>
                            <th className="p-3 text-left font-medium">Admin</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-3 font-medium">Floor</td>
                            <td className="p-3">
                              {(() => {
                                const val = getAdminValue(selectedProperty, ["floor_admin", "floors_admin", "floor", "floors", "floorAdmin", "floorsAdmin"]);
                                return hasUsableValue(val) ? val : "N/A";
                              })()}
                            </td>
                          </tr>

                          <tr className="border-b">
                            <td className="p-3 font-medium">Land Use</td>
                            <td className="p-3">
                              {(() => {
                                const val = getAdminValue(selectedProperty, ["landuse_admin", "land_use_admin", "landuse", "land_use", "landuseAdmin", "landUseAdmin"]);
                                return hasUsableValue(val) ? val : "N/A";
                              })()}
                            </td>
                          </tr>

                          <tr className="border-b">
                            <td className="p-3 font-medium">Land Area</td>
                            <td className="p-3">
                              {(() => {
                                const val = getAdminValue(selectedProperty, ["landarea_admin", "land_area_admin", "landarea", "land_area", "landareaAdmin", "landAreaAdmin"]);
                                return hasUsableValue(val) ? `${formatTwoDecimals(val) || val} sq.yards` : "N/A";
                              })()}
                            </td>
                          </tr>

                          <tr className="border-b">
                            <td className="p-3 font-medium">Covered Area</td>
                            <td className="p-3">
                              {(() => {
                                const val = getAdminValue(selectedProperty, ["coveredarea_admin", "covered_area_admin", "coveredarea", "covered_area", "coveredareaAdmin", "coveredAreaAdmin"]);
                                return hasUsableValue(val) ? `${formatTwoDecimals(val) || val} sq.ft` : "N/A";
                              })()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                  </div>

                  {/* Timeline / Chat History */}
                  {SHOW_ACTIVITY_HISTORY && <div className="mb-6">
                    {/* <h3 className="text-lg font-semibold mb-3 text-gray-800">Activity History & Chat</h3> */}
                    {propertyTimeline.timeline && propertyTimeline.timeline.length > 0 ? (
                      <div className="space-y-3">
                        {propertyTimeline.timeline.filter((item) => {
                          // Filter out the assignment comment
                          const isAssignmentComment =
                            (item.text && item.text.includes("Assigned via bulk assignment")) ||
                            (item.action && item.action.includes("assign"));
                          return !isAssignmentComment;
                        }).map((item) => {
                          const isInspector = item.user_role === "inspector";
                          const isSupervisor = item.user_role === "supervisor";

                          return (
                            <div key={item.id} className="flex gap-3">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isInspector ? "bg-blue-500" : isSupervisor ? "bg-green-500" : "bg-gray-500"
                                }`}>
                                {item.user_name ? item.user_name.charAt(0).toUpperCase() : "?"}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-800">
                                    {item.user_name || "Unknown User"}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(item.created_at).toLocaleString()}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                    {item.stage}
                                  </span>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  {item.type === "comment" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                  {item.type === "supervisor" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                  {item.type === "inspector_explanation" && (
                                    <div className="text-sm text-gray-700">
                                      <p className="font-medium mb-2">Overall: {item.overall}</p>
                                      {item.rows.floor && <p>Floor: {item.rows.floor}</p>}
                                      {item.rows.landuse && <p>Land Use: {item.rows.landuse}</p>}
                                      {item.rows.landarea && <p>Land Area: {item.rows.landarea}</p>}
                                      {item.rows.coveredarea && <p>Covered Area: {item.rows.coveredarea}</p>}
                                    </div>
                                  )}
                                  {item.type === "action" && (
                                    <p className="text-sm text-gray-700">
                                      {item.action.replace(/_/g, " ")}
                                      {item.details && (
                                        <span className="ml-2 text-gray-500">
                                          - {JSON.stringify(item.details)}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-4">No activity yet.</p>
                    )}
                  </div>}
                </div>

                {/* Modal Footer */}
                {selectedProperty.status !== "flagged" &&
                  selectedProperty.status !== "resolved" && (
                    <div className="sticky bottom-0 border-t bg-gray-50 p-6">
                      <div className="flex justify-center gap-6">
                        <button
                          onClick={() => handleAccept(selectedProperty.id)}
                          className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
                        >
                          Accept Property
                        </button>
                        <button
                          onClick={() => handleFlag(selectedProperty.id)}
                          className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
                        >
                          Flag Property
                        </button>
                      </div>
                    </div>
                  )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Fullscreen Image Modal with rotate */}
        <AnimatePresence>
          {showFullscreenImage && (
            <motion.div
              className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeFullscreenImage}
            >
              <div className="absolute top-6 right-6 flex space-x-4 z-10">
                <button
                  onClick={handleRotate}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                  title="Rotate Image"
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
                {rotation !== 0 && (
                  <button
                    onClick={handleSaveRotation}
                    disabled={savingImageRotation}
                    className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full transition-all shadow-lg flex items-center justify-center"
                    title="Save Rotation"
                  >
                    {savingImageRotation ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                )}
                <button
                  onClick={closeFullscreenImage}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                >
                  <span className="text-white text-2xl font-bold">×</span>
                </button>
              </div>
              <motion.div
                initial={{ scale: 0.9, opacity: 0, rotate: 0 }}
                animate={{ scale: 1, opacity: 1, rotate: rotation }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={fullscreenImageUrl}
                  alt="Fullscreen property"
                  className="max-w-[95vw] max-h-[95vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Comment Modal */}
        <AnimatePresence>
          {showCommentBox && (
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                  Enter Reason for Flagging
                </h3>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Type your reason here..."
                  className="w-full border border-gray-300 rounded-lg p-3 h-28 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                />
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    onClick={() => setShowCommentBox(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitFlag}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Submit
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Property;
